import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { FileDown } from 'lucide-react';
import { formatTermMonths } from '../../../products/lib/billing-term';
import { formatEuro } from '../../lib/format';
import { OfferCheckoutAddOnResponseDto, OfferCheckoutProductResponseDto } from '../../types/response';
import OfferSummaryItem from '../offer-summary-item/offer-summary-item';
import RatingBadge from '../rating-badge/rating-badge';

const PRODUCT_ITEM_VALUE = 'product';

export interface OfferSummaryProps {
  product: OfferCheckoutProductResponseDto;
  addOns: OfferCheckoutAddOnResponseDto[];
  /** Minimum contract duration of `product.term`. */
  termMonths: number;
  /** `null` if no offer PDF is attached. */
  offerFileName: string | null;
  /** Without it (preview) the link is shown but does nothing. */
  onDownloadOfferFile?: () => void;
}

/**
 * Left side of the checkout, also used as the admin live preview – props only, no requests.
 * Desktop: fills the height the page gives it and scrolls on its own. Mobile: grows with its content.
 */
const OfferSummary = ({ product, addOns, termMonths, offerFileName, onDownloadOfferFile }: OfferSummaryProps) => (
  <section className="w-full rounded-2xl bg-[#E8F6F6] lg:h-full lg:overflow-y-auto">
    <div className="mx-auto flex min-h-full max-w-sm flex-col justify-center gap-8 px-6 py-10 lg:px-8 lg:py-16">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-[#082729]/50">Mindestlaufzeit: {formatTermMonths(termMonths)}</p>
        <div className="mb-2 flex items-end gap-2">
          <span className="text-4xl font-bold leading-none text-[#082729]">{formatEuro(product.monthlyPriceCents)}</span>
          <span className="text-xs leading-tight text-[#082729]/50">
            Pro
            <br />
            Monat zzgl. USt.
          </span>
        </div>
        {product.additionalConditions && <p className="whitespace-pre-line text-xs text-[#082729]/50">{product.additionalConditions}</p>}
        <RatingBadge />
      </div>

      <AccordionPrimitive.Root type="multiple" defaultValue={[PRODUCT_ITEM_VALUE]} className="divide-y divide-[#082729]/15">
        <OfferSummaryItem
          value={PRODUCT_ITEM_VALUE}
          title={product.title}
          amount={formatEuro(product.monthlyPriceCents)}
          description={product.description}
        />
        {addOns.map((addOn, index) => (
          // Add-ons have no id in the public response; the list never reorders.
          <OfferSummaryItem key={index} value={`add-on-${index}`} title={addOn.title} amount="inklusive" description={addOn.description} />
        ))}
      </AccordionPrimitive.Root>

      {offerFileName && (
        <button
          type="button"
          onClick={onDownloadOfferFile}
          disabled={!onDownloadOfferFile}
          className="flex items-center gap-3 self-start text-left text-sm text-[#082729] disabled:cursor-default enabled:hover:underline">
          <FileDown className="h-4 w-4 shrink-0" />
          <span>
            <span className="font-bold">Angebot als PDF herunterladen</span>
            <span className="block text-xs text-[#082729]/50">{offerFileName}</span>
          </span>
        </button>
      )}
    </div>
  </section>
);

export default OfferSummary;
